import { AgnosticTheme } from '@awazevr/design-system.components.awaze-theme-provider'
import Head from 'next/head'
import { JSX, useContext } from 'react'
import { GlobalProps } from 'src/utils/globalProps'
import { buildSeoCanonical, getAssetUrl } from 'src/utils/helpers/helpers'
import { ThemeContext } from 'styled-components'
import { ISeoMetadataFields } from '../../../@types/generated/contentful-global'
import { LocaleSlug, HrefLangLinks } from '../Utils/HrefLangLinks'

interface CustomHeadProps {
  host?: string
  url: string
  seoData: ISeoMetadataFields
  schemaMarkup?: string
  hrefLangs: LocaleSlug
  seoImage?: string
}

export const CustomHead: React.FC<CustomHeadProps> = ({
  host,
  url,
  seoData,
  schemaMarkup,
  hrefLangs,
  seoImage,
}): JSX.Element => {
  const themeContext = useContext(ThemeContext) || AgnosticTheme
  const { brand } = useContext(GlobalProps)

  const stringManifest = {
    name: seoData.seoTitle,
    short_name: brand,
    display: 'standalone',
    orientation: 'portrait',
    description: seoData.seoDescription,
    background_color: themeContext.ColorWhiteOpacity100,
    theme_color: themeContext.ColorThemeDark200,
    icons: [
      {
        src: getAssetUrl(`/brand/${brand}/favicon_${brand}_x48.png`),
        sizes: '48x48',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_${brand}_x72.png`),
        sizes: '72x72',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_${brand}_x96.png`),
        sizes: '96x96',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_${brand}_x128.png`),
        sizes: '128x128',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_${brand}_x192.png`),
        sizes: '192x192',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_${brand}_x384.png`),
        sizes: '384x384',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_${brand}_x512.png`),
        sizes: '512x512',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_${brand}_HD.png`),
        sizes: '16x16',
        type: 'image/png',
      },
      {
        src: getAssetUrl(`/brand/${brand}/favicon_${brand}.svg`),
        sizes: 'any',
        type: 'image/svg+xml',
      },
    ],
  }

  const { seoNoindex, seoNofollow, seoTitle, seoDescription, seoKeywords } =
    seoData

  const seoCanonical = seoData.seoCanonical ?? buildSeoCanonical(host, url)

  return (
    <Head>
      {seoTitle && <title>{seoTitle}</title>}
      {seoCanonical && <link rel="canonical" href={seoCanonical} />}
      {HrefLangLinks({ hrefLangs, brand })}
      {(seoNoindex || seoNofollow) && (
        <meta
          name="robots"
          content={`${seoNoindex ? 'noindex' : ''} ${
            seoNofollow ? 'nofollow' : ''
          }`.trim()}
        />
      )}
      <link
        rel="manifest"
        href={`data:application/json;charset=utf-8,${JSON.stringify(
          stringManifest,
        )}`}
      />
      <link
        rel="apple-touch-icon"
        sizes="192x192"
        href={getAssetUrl(`/brand/${brand}/favicon_${brand}_x192.png`)}
      />
      <link
        rel="icon"
        type="image/png"
        href={getAssetUrl(`/brand/${brand}/favicon_${brand}_x48.png`)}
      />
      <link
        rel="icon"
        type="image/svg+xml"
        href={getAssetUrl(`/brand/${brand}/favicon_${brand}.svg`)}
      />
      <meta
        name="theme-color"
        content={themeContext.ColorThemeBackgroundHeader}
      ></meta>
      {seoDescription && <meta name="description" content={seoDescription} />}
      {seoKeywords?.length > 0 && (
        <meta name="keywords" content={seoKeywords?.join(',')} />
      )}
      {seoImage && <meta property="og:image" content={seoImage} />}
      {seoImage && <meta name="twitter:image" content={seoImage} />}
      {seoImage && <meta name="twitter:card" content="summary_large_image" />}
      {schemaMarkup && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: schemaMarkup }}
        />
      )}
    </Head>
  )
}
